import type { FaqEntry } from "../../../lib/help/types";

export const faqEntries: FaqEntry[] = [
  {
    id: "free-trial",
    category: "buying-trial",
    question: "Is there a free trial?",
    answer:
      "Yes. Every school gets a full 30-day trial with every feature unlocked — no card required. You can import your real borrower list, catalogue your books, and run real loans during the trial. If you decide to buy, all your data carries over. If you don't, nothing is lost: you can still export your data as a CSV.",
    tags: ["trial", "pricing", "free", "demo"],
    relatedGuides: ["quick-start-first-loan", "installing-bookburrow"],
  },
  {
    id: "one-time-price",
    category: "buying-trial",
    question: "What does a licence cost and is it a one-time purchase?",
    answer:
      "A licence is £149 once per school and includes 12 months of updates and support from the date of purchase. After that, you can optionally renew for £49 a year to keep getting updates and support. If you don't renew, the version of BookBurrow you already have keeps working forever — no features disabled, no data locked. There are no per-pupil fees and no \"essential\" add-ons.",
    tags: ["pricing", "licence", "renewal", "cost"],
  },
  {
    id: "how-to-buy",
    category: "buying-trial",
    question: "How do I actually buy a licence?",
    answer:
      "Sign up for the trial, then in the portal go to Settings → Upgrade. Checkout is handled securely by Stripe and accepts school card payments and invoiced BACS. You'll receive the licence key by email within a few minutes — or, for invoiced orders, once the payment clears.",
    tags: ["purchase", "stripe", "invoice", "bacs", "checkout"],
    relatedGuides: ["activating-your-licence"],
  },
  {
    id: "purchase-order",
    category: "buying-trial",
    question: "Can we pay by purchase order / invoice?",
    answer:
      "Yes. Email purchasing@bookburrow.uk with your school name, address, and PO number and we'll send back a proper invoice with BACS details. We're set up as a UK supplier and happy to follow whatever your finance office needs.",
    tags: ["invoice", "po", "purchase order", "finance"],
  },
  {
    id: "which-os",
    category: "installation",
    question: "Which operating systems does BookBurrow run on?",
    answer:
      "BookBurrow runs on Windows 10 or newer (64-bit) and on recent Linux distributions (Ubuntu 22.04+, Debian 12+). You only need about 100 MB of free disk space and any laptop or desktop sold in the last ten years will handle it comfortably.",
    tags: ["windows", "linux", "system requirements", "install"],
    relatedGuides: ["installing-bookburrow"],
  },
  {
    id: "install-without-admin",
    category: "installation",
    question: "Do I need admin rights or IT help to install it?",
    answer:
      "For most schools, no — the Windows installer is a standard .msi and can be installed per-user. If your school locks down software installs, send your IT contact a link to our Installing BookBurrow guide: they'll see it's a signed .msi with no background services or telemetry, which usually gets approval quickly.",
    tags: ["install", "admin", "it", "deploy"],
    relatedGuides: ["installing-bookburrow"],
  },
  {
    id: "offline",
    category: "installation",
    question: "Do I need Wi-Fi or an internet connection?",
    answer:
      "No. BookBurrow is a proper desktop app: lending, returns, searching, and reports all work completely offline. Internet is only used occasionally to look up new book details by ISBN and to check for updates. Your library can carry on running even if the school network goes down.",
    tags: ["offline", "wifi", "internet", "network"],
    relatedGuides: ["installing-bookburrow"],
  },
  {
    id: "one-licence-many-computers",
    category: "licences",
    question: "Can I use one licence on several computers?",
    answer:
      "A licence covers one school and can be activated on one computer at a time. If you need to move to a different machine, you can deactivate the old one from Settings → Licence and activate the new one in a couple of clicks. If you need BookBurrow on several computers simultaneously, contact us about a multi-seat licence.",
    tags: ["licence", "multi", "seats", "deactivate", "transfer"],
    relatedGuides: ["transferring-to-a-new-computer", "activating-your-licence"],
  },
  {
    id: "replace-computer",
    category: "licences",
    question: "What if the library computer dies or gets replaced?",
    answer:
      "No problem. Your latest backup (which BookBurrow takes automatically every 15 minutes) contains everything: borrowers, books, loan history, and settings. Install BookBurrow on the new computer, restore the backup, reactivate your licence key, and you're running again — usually within 15 minutes of unboxing the new machine.",
    tags: ["replace", "hardware", "backup", "restore", "transfer"],
    relatedGuides: ["transferring-to-a-new-computer", "restoring-from-a-backup"],
  },
  {
    id: "activation-machine-limit",
    category: "licences",
    question: "I've used up my activations — how do I free one up?",
    answer:
      "Every licence comes with a generous activation allowance, but if you hit the limit (usually after several machine changes) email support@bookburrow.uk with your licence key and we'll reset it for you, normally within a few hours during the school day.",
    tags: ["activation", "limit", "reset", "licence"],
    relatedGuides: ["activating-your-licence", "transferring-to-a-new-computer"],
  },
  {
    id: "data-stored-where",
    category: "data-privacy",
    question: "Where is my library data stored?",
    answer:
      "All of it lives on your computer, in a single database file in your user folder. Nothing is ever uploaded to our servers. This is why BookBurrow works offline and why you — not us — are in full control of your pupil data. Your backup location (OneDrive, SharePoint, USB, network share) is whatever you choose in Settings.",
    tags: ["data", "privacy", "storage", "cloud", "gdpr"],
    relatedGuides: ["configuring-automatic-backups"],
  },
  {
    id: "gdpr",
    category: "data-privacy",
    question: "Is BookBurrow GDPR-compliant?",
    answer:
      "Yes. Because all pupil data stays on your school's own computer, your school remains the data controller and BookBurrow is not a processor of personal data at all. The app also automatically purges historical loan records at the end of each academic year (configurable) so you only keep what you need.",
    tags: ["gdpr", "privacy", "data protection", "pupil data"],
    relatedGuides: ["configuring-automatic-backups", "pin-access-and-roles"],
  },
  {
    id: "pupil-names",
    category: "data-privacy",
    question: "Does BookBurrow send pupil names anywhere?",
    answer:
      "No. Pupil names never leave the device. The only data BookBurrow sends externally is anonymous ISBN lookups (to fetch book titles and authors from open book databases), and completely optional crash reports that contain no pupil information.",
    tags: ["privacy", "pupil", "telemetry", "isbn"],
  },
  {
    id: "scanner-type",
    category: "hardware",
    question: "What kind of barcode scanner do I need?",
    answer:
      "Any standard USB barcode scanner that works as a \"keyboard wedge\" — meaning it acts like a tiny keyboard and types the barcode number. You can buy one for about £20 on Amazon. We don't sell scanners ourselves, but our Scanning books guide lists a few models that schools have had good luck with.",
    tags: ["scanner", "barcode", "usb", "hardware"],
    relatedGuides: ["scanning-books-isbn-lookup", "barcode-scanner-not-working"],
  },
  {
    id: "label-printer",
    category: "hardware",
    question: "Do I need a label printer?",
    answer:
      "No. Most schools print borrower cards and spine labels on plain A4 sheets using a normal printer — BookBurrow lays them out for you. If you already have a Dymo or Brother label printer you can use that too, but it's absolutely not required.",
    tags: ["printer", "labels", "spine", "borrower card"],
    relatedGuides: ["printing-borrower-cards"],
  },
  {
    id: "existing-barcodes",
    category: "hardware",
    question: "Can I use barcodes the previous system printed?",
    answer:
      "Usually yes. If your old labels use standard barcode formats (Code 39, Code 128, or EAN), BookBurrow will read them the same as any new ones. You can even bulk-import the old barcode numbers as part of your catalogue CSV so no re-labelling is needed.",
    tags: ["barcode", "migration", "existing", "labels"],
    relatedGuides: ["scanning-books-isbn-lookup"],
  },
  {
    id: "import-borrowers",
    category: "day-to-day",
    question: "How do I get my pupils into BookBurrow?",
    answer:
      "Export a pupil list from your school's MIS (SIMS, Arbor, Bromcom, or similar) as a CSV, then use File → Import borrowers in BookBurrow. The importer previews the data, lets you map columns, and highlights any problems before anything is saved. Our borrower import guide walks through this step by step for each MIS.",
    tags: ["import", "sims", "arbor", "bromcom", "borrowers", "csv", "mis"],
    relatedGuides: ["importing-from-sims-arbor-bromcom", "adding-borrowers-manually"],
  },
  {
    id: "year-end",
    category: "day-to-day",
    question: "What do I do at the end of the school year?",
    answer:
      "Run the Year-end wizard in Settings → Year end. It promotes every pupil up a year group, graduates your Year 6 / Year 11 leavers, tidies loan history per your retention settings, and produces a nice summary report for your head teacher. The whole thing takes about two minutes.",
    tags: ["year end", "promotion", "leavers", "annual"],
    relatedGuides: ["year-end-promotion"],
  },
  {
    id: "reports",
    category: "day-to-day",
    question: "What reports can BookBurrow produce?",
    answer:
      "Out of the box: late books, most popular titles, stock summary, reading activity by year group and class, recently added books, never-lent books, and a pupils-borrowing-nothing list. Each one prints neatly and also exports to Excel if you want to play with the numbers yourself.",
    tags: ["reports", "excel", "late", "popular", "reading activity"],
    relatedGuides: ["running-reports"],
  },
  {
    id: "scanner-not-working",
    category: "troubleshooting",
    question: "My scanner isn't reading barcodes — what now?",
    answer:
      "Ninety percent of the time it's one of three things: the scanner isn't in \"keyboard wedge\" mode, the scan field in BookBurrow isn't focused, or the cable isn't fully seated. Our Barcode scanner not working guide walks through each in order. If that doesn't sort it, email support@bookburrow.uk with the scanner model and we'll help.",
    tags: ["scanner", "barcode", "broken", "troubleshooting"],
    relatedGuides: ["barcode-scanner-not-working", "scanning-books-isbn-lookup"],
  },
  {
    id: "lost-backup",
    category: "troubleshooting",
    question: "I've lost data — can I restore from a backup?",
    answer:
      "Almost certainly yes. BookBurrow keeps automatic backups every 15 minutes in the folder you configured (OneDrive, SharePoint, USB, or network share), with 30-day retention by default. Open File → Restore from backup, pick the most recent good file, and you'll be back to that point in time. See our Restoring from a backup guide for a walkthrough.",
    tags: ["restore", "backup", "data loss", "recovery"],
    relatedGuides: ["restoring-from-a-backup", "configuring-automatic-backups"],
  },
  {
    id: "forgot-pin",
    category: "troubleshooting",
    question: "I've forgotten the admin PIN — am I locked out?",
    answer:
      "No. On the BookBurrow PIN screen, click 'Forgot PIN?' to get a one-day challenge code, then visit bookburrow.uk/pin-recovery, enter your licence key and that challenge code, and we'll give you a response code you can type back into BookBurrow to set a new PIN. The whole process takes about a minute and doesn't need any email.",
    tags: ["pin", "lock", "password", "admin", "reset", "recovery"],
    relatedGuides: ["pin-access-and-roles"],
  },
  {
    id: "update-app",
    category: "troubleshooting",
    question: "How do I update to the latest version?",
    answer:
      "BookBurrow checks for updates automatically when you start it (if you're online) and will prompt you to install. You can also check manually in Help → Check for updates. Updates never touch your data — they only replace the app itself, so there's no risk to your library.",
    tags: ["update", "version", "upgrade", "latest"],
  },
];
